import {parseFileAnchor} from '@github-ui/blob-anchor'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import linkButtonStyles from '@github-ui/code-view-shared/css/LinkButton.module.css'
import {useCurrentRepository} from '@github-ui/current-repository'
import {blamePath, blobPath} from '@github-ui/paths'
import {useNavigate} from '@github-ui/use-navigate'
import {LinkButton} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {Box, TextInput} from '@primer/styled-react'
import {useEffect, useEffectEvent, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {useCurrentBlob} from '../../hooks/CurrentBlob'
import {useCursorEndLineNumber, useCursorStartLineNumber, useIsCursorEnabled} from '../../hooks/use-cursor-navigation'
import {scrollLineIntoView} from '../../hooks/use-scroll-line-into-view'

export function useWorkflowRedirectUrl() {
  const blobPayload = useCurrentBlob()
  return blobPayload.workflowRedirectUrl
}

export function GoToLineDialog({
  onBlamePage,
  onDismiss,
  maxLineNumber,
}: {
  onBlamePage: boolean
  onDismiss: () => void
  maxLineNumber?: number
}) {
  const navigate = useNavigate()
  const shouldUseCursor = useIsCursorEnabled()
  const startLineNum = useCursorStartLineNumber()
  const endLineNum = useCursorEndLineNumber()
  const lineNumberRef = useRef(shouldUseCursor ? startLineNum : 1)
  const validRef = useRef(true)
  const [changeDescribeBy, setChangeDescribeBy] = useState(true)
  const secondLineNumberRef = useRef(startLineNum !== endLineNum && shouldUseCursor ? endLineNum : null)
  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()
  const inputBoxRef = useRef<HTMLInputElement>(null)

  const hash = useRef(
    // eslint-disable-next-line react-hooks/refs
    lineNumberRef.current
      ? // eslint-disable-next-line react-hooks/refs
        `#L${lineNumberRef.current}${secondLineNumberRef.current ? `-L${secondLineNumberRef.current}` : ''}`
      : '',
  )
  const href = onBlamePage
    ? blamePath({
        repo: repo.name,
        owner: repo.ownerLogin,
        filePath: path,
        commitish: refInfo.name,
      })
    : blobPath({
        repo: repo.name,
        owner: repo.ownerLogin,
        filePath: path,
        commitish: refInfo.name,
      })

  const focusInputBoxRefOnLoad = useEffectEvent(() => {
    if (inputBoxRef && inputBoxRef.current) {
      inputBoxRef.current.value = shouldUseCursor
        ? `${startLineNum}${endLineNum !== startLineNum ? `-${endLineNum}` : ''}`
        : '1'

      inputBoxRef.current.focus()
    }
  })
  useEffect(() => {
    focusInputBoxRefOnLoad()
  }, [])

  function handleInputChange(inputValue: string) {
    let isValid = true
    if (inputValue.trim() === '') {
      lineNumberRef.current = 1
    }
    if (inputValue.startsWith('-') && maxLineNumber) {
      const intValue = parseInt(inputValue, 10)
      if (!Number.isNaN(intValue) && intValue < 0) {
        let numberToGoTo = maxLineNumber + intValue + 1
        if (numberToGoTo <= 0) {
          numberToGoTo = 1
          isValid = false
        }
        lineNumberRef.current = numberToGoTo
      }
    } else if (inputValue.includes('-')) {
      const [firstLine, secondLine] = inputValue.split('-')
      const firstIntValue = parseInt(firstLine!, 10)
      const secondIntvalue = parseInt(secondLine!, 10)
      if (!Number.isNaN(firstIntValue) && firstIntValue > 0) {
        lineNumberRef.current = maxLineNumber ? Math.min(firstIntValue, maxLineNumber) : firstIntValue
        isValid = maxLineNumber !== undefined && firstIntValue <= maxLineNumber
      }
      if (!Number.isNaN(secondIntvalue) && secondIntvalue > 0) {
        secondLineNumberRef.current = maxLineNumber ? Math.min(secondIntvalue, maxLineNumber) : secondIntvalue
        isValid = isValid && maxLineNumber !== undefined && secondIntvalue <= maxLineNumber
      }
    } else {
      const intValue = parseInt(inputValue, 10)
      if (!Number.isNaN(intValue) && intValue > 0) {
        lineNumberRef.current = maxLineNumber ? Math.min(intValue, maxLineNumber) : intValue
        isValid = maxLineNumber !== undefined && intValue <= maxLineNumber
      } else {
        isValid = inputValue !== '' ? false : true
      }
    }
    validRef.current = isValid
    if (isValid && !changeDescribeBy) {
      setChangeDescribeBy(true)
    }
    //fix hitting enter on the input box updating even if it isn't valid
    hash.current = `#L${lineNumberRef.current}${secondLineNumberRef.current ? `-L${secondLineNumberRef.current}` : ''}`
  }

  const handleGoToLine = (url: string, newHash: string) => {
    // update our url and trigger any necessary changes on path/hash change (namely highlighting)
    navigate(`${url}${newHash}`)
    // hash changes don't automatically scroll to the line as of commit 252468
    const anchInfo = parseFileAnchor(newHash)
    if (!anchInfo.blobRange?.start?.line || !validRef.current) {
      setChangeDescribeBy(validRef.current)
      setTimeout(() => {
        inputBoxRef.current?.focus()
      }, 25)
      return
    }
    scrollLineIntoView({line: anchInfo.blobRange.start.line})

    onDismiss()
  }

  //creating a portal because there were z-index conflicts which are resolved by creating a portal for the dialog
  //TODO: change the 'Go' linkbutton below back to a button with 'as="a"' once the primer react bug is fixed
  return createPortal(
    <Dialog isOpen onDismiss={onDismiss}>
      <Dialog.Header>Jump to line</Dialog.Header>
      <Box sx={{display: 'flex', pl: 3, pr: 3, pt: 3, pb: changeDescribeBy ? 3 : 0}}>
        <Box sx={{display: 'flex', flexGrow: 1, mr: 2}}>
          <TextInput
            ref={inputBoxRef}
            aria-invalid={!changeDescribeBy}
            aria-describedby={changeDescribeBy ? '' : 'goToLineErrorValidation'}
            sx={{flexGrow: 1, pr: 2}}
            placeholder="Jump to line..."
            onChange={e => {
              const value = e.target.value
              if (inputBoxRef && inputBoxRef.current) {
                inputBoxRef.current.value = value
              }
              handleInputChange(value)
            }}
            onFocus={() => {
              if (inputBoxRef && inputBoxRef.current) {
                inputBoxRef.current.select()
              }
            }}
            onKeyDown={e => {
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (e.key !== 'Enter') return
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (e.key === 'Enter' && !validRef.current) {
                setChangeDescribeBy(validRef.current)
                setTimeout(() => {
                  inputBoxRef.current?.focus()
                }, 25)
                return
              }
              handleGoToLine(href, hash.current)
            }}
          />
        </Box>
        <LinkButton
          // eslint-disable-next-line react-hooks/refs
          href={changeDescribeBy ? href + hash.current : undefined}
          onClick={(e: React.MouseEvent) => {
            // The default click action for a semantic link is to go to the URL specified in `href`, but we need to
            // ensure the custom scroll-to-line behavior executes. As a result, preventing the default event behavior
            // here and handling the navigation ourselves keeps behavior consistent.
            e.preventDefault()
            handleGoToLine(href, hash.current)
          }}
          className={linkButtonStyles.linkButton}
        >
          Go
        </LinkButton>
      </Box>
      {!changeDescribeBy && (
        <Box
          role="alert"
          id="goToLineErrorValidation"
          sx={{display: 'flex', p: 2, justifyContent: 'center', color: 'red'}}
        >
          Invalid line number
        </Box>
      )}
    </Dialog>,
    document.body,
  )
}
